import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getCertificateTemplatesAsync } from '@share/api';
import type {
  ICertificateTemplatesParams,
  ICertificateTemplatesResponse,
} from '@share/common-types';
import { AppThunk } from '@share/utils';

export interface ICertificateTemplatesSearchState {
  data: ICertificateTemplatesResponse | null;
  loading: boolean;
  loadingMore: boolean;
  error: string;
}

const initialState: ICertificateTemplatesSearchState = {
  data: null,
  loading: false,
  loadingMore: false,
  error: '',
};

const certificateTemplatesSearchSlice = createSlice({
  name: 'certificateTemplatesSearch',
  initialState,
  reducers: {
    setLoading: (state: ICertificateTemplatesSearchState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setLoadingMore: (
      state: ICertificateTemplatesSearchState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.loadingMore = payload;
    },
    setError: (state: ICertificateTemplatesSearchState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setCertificateTemplatesData: (
      state: ICertificateTemplatesSearchState,
      { payload }: PayloadAction<ICertificateTemplatesResponse | null>,
    ) => {
      state.data = payload;
    },
    appendCertificateTemplatesData: (
      state: ICertificateTemplatesSearchState,
      { payload }: PayloadAction<ICertificateTemplatesResponse>,
    ) => {
      if (!state.data) {
        state.data = payload;
        return;
      }
      state.data = {
        ...payload,
        items: [...state.data.items, ...payload.items],
      };
    },
    clearCertificates: (state: ICertificateTemplatesSearchState) => {
      state.data = null;
      state.error = '';
    },
  },
});

export const certificateTemplatesSearchActions = certificateTemplatesSearchSlice.actions;

export const certificateTemplatesSearchReducer = certificateTemplatesSearchSlice.reducer;

export interface FetchCertificateTemplatesOptions {
  append?: boolean;
}

export const fetchCertificateTemplates = (
  params: ICertificateTemplatesParams,
  options: FetchCertificateTemplatesOptions = {},
): AppThunk => {
  return async (dispatch) => {
    const { append = false } = options;

    if (append) {
      dispatch(certificateTemplatesSearchActions.setLoadingMore(true));
    } else {
      dispatch(certificateTemplatesSearchActions.setLoading(true));
      dispatch(certificateTemplatesSearchActions.setError(''));
    }

    try {
      const { data } = await getCertificateTemplatesAsync(params);
      if (append) {
        dispatch(certificateTemplatesSearchActions.appendCertificateTemplatesData(data));
      } else {
        dispatch(certificateTemplatesSearchActions.setCertificateTemplatesData(data));
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      dispatch(certificateTemplatesSearchActions.setError(message));
      if (!append) {
        dispatch(certificateTemplatesSearchActions.setCertificateTemplatesData(null));
      }
    } finally {
      if (append) {
        dispatch(certificateTemplatesSearchActions.setLoadingMore(false));
      } else {
        dispatch(certificateTemplatesSearchActions.setLoading(false));
      }
    }
  };
};
